import { useState, type ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import { authorizedFetch } from "@/lib/api"
import { cn } from "@/lib/utils"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

// ── Types ──────────────────────────────────────────────────────────────────

type WorkLine = "batea" | "nacional" | "internacional"

type OpsStatsRow = {
  workLine: WorkLine
  shipments: number
  trips: number
  totalKm: number
  withCargoKm: number
  tripsWithCargo: number
  tripsWithoutCargo: number
}

type OpsStats = {
  rows: OpsStatsRow[]
  totals: {
    shipments: number
    trips: number
    totalKm: number
    withCargoKm: number
    tripsWithCargo: number
    tripsWithoutCargo: number
  }
}

type Period = "today" | "week" | "month"

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? value as Record<string, unknown>
    : {}
}

function toStatNumber(value: unknown): number {
  const number = Number(value ?? 0)
  return Number.isFinite(number) ? number : 0
}

function isWorkLine(value: unknown): value is WorkLine {
  return value === "batea" || value === "nacional" || value === "internacional"
}

function normalizeOpsStats(payload: unknown): OpsStats {
  const data = asRecord(payload)
  const rawRows = Array.isArray(data.rows) ? data.rows : []
  const rows = rawRows.flatMap((value): OpsStatsRow[] => {
    const row = asRecord(value)
    const workLine = row.workLine ?? row.work_line
    if (!isWorkLine(workLine)) return []

    return [{
      workLine,
      shipments: toStatNumber(row.shipments),
      trips: toStatNumber(row.trips),
      totalKm: toStatNumber(row.totalKm ?? row.total_km),
      withCargoKm: toStatNumber(row.withCargoKm ?? row.with_cargo_km),
      tripsWithCargo: toStatNumber(row.tripsWithCargo ?? row.trips_with_cargo),
      tripsWithoutCargo: toStatNumber(row.tripsWithoutCargo ?? row.trips_without_cargo),
    }]
  })

  const rawTotals = asRecord(data.totals)
  return {
    rows,
    totals: {
      shipments: toStatNumber(rawTotals.shipments),
      trips: toStatNumber(rawTotals.trips),
      totalKm: toStatNumber(rawTotals.totalKm ?? rawTotals.total_km),
      withCargoKm: toStatNumber(rawTotals.withCargoKm ?? rawTotals.with_cargo_km),
      tripsWithCargo: toStatNumber(rawTotals.tripsWithCargo ?? rawTotals.trips_with_cargo),
      tripsWithoutCargo: toStatNumber(rawTotals.tripsWithoutCargo ?? rawTotals.trips_without_cargo),
    },
  }
}

// ── Date helpers ───────────────────────────────────────────────────────────

// Uses local date methods to avoid UTC offset issues (e.g. ART = UTC-3).
function toLocalDateStr(d: Date): string {
  const y  = d.getFullYear()
  const m  = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${dd}`
}

function getPeriodDates(period: Period): { from: string; to: string } {
  const now   = new Date()
  const today = toLocalDateStr(now)

  if (period === "today") {
    return { from: today, to: today }
  }

  if (period === "week") {
    const day = now.getDay() // 0 = Sunday
    const diffToMonday = day === 0 ? 6 : day - 1
    const monday = new Date(now)
    monday.setDate(now.getDate() - diffToMonday)
    return { from: toLocalDateStr(monday), to: today }
  }

  // month
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  return { from: toLocalDateStr(firstOfMonth), to: today }
}

// ── Display helpers ────────────────────────────────────────────────────────

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  batea: "Batea",
  nacional: "Nacional",
  internacional: "Internacional",
}

const WORK_LINE_BADGE_CLASS: Record<WorkLine, string> = {
  batea: "bg-blue-100 text-blue-700",
  nacional: "bg-emerald-100 text-emerald-700",
  internacional: "bg-violet-100 text-violet-700",
}

function efficiency(tripsWithCargo: number, trips: number): string {
  if (trips === 0) return "—"
  return `${Math.round((tripsWithCargo / trips) * 100)}%`
}

function efficiencyPct(tripsWithCargo: number, trips: number): number {
  if (trips === 0) return 0
  return Math.round((tripsWithCargo / trips) * 100)
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SubSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export function OpsStatsSection() {
  const [period, setPeriod] = useState<Period>("month")

  const { from, to } = getPeriodDates(period)

  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard", "ops-stats", from, to],
    queryFn: async () => {
      const url = new URL(`${API_URL}/api/dashboard/ops-stats`)
      url.searchParams.set("from", from)
      url.searchParams.set("to", to)
      const res = await authorizedFetch(url.toString())
      return normalizeOpsStats(await res.json())
    },
    staleTime: 30_000,
  })

  const rows = stats?.rows ?? []
  const totals = stats?.totals ?? {
    shipments: 0,
    trips: 0,
    totalKm: 0,
    withCargoKm: 0,
    tripsWithCargo: 0,
    tripsWithoutCargo: 0,
  }

  // Ensure all three work lines are represented (fill missing ones with zeros)
  const ALL_WORK_LINES: WorkLine[] = ["batea", "nacional", "internacional"]
  const rowMap = new Map(rows.map((r) => [r.workLine, r]))
  const normalizedRows: OpsStatsRow[] = ALL_WORK_LINES.map(
    (wl) =>
      rowMap.get(wl) ?? {
        workLine: wl,
        shipments: 0,
        trips: 0,
        totalKm: 0,
        withCargoKm: 0,
        tripsWithCargo: 0,
        tripsWithoutCargo: 0,
      },
  )

  return (
    <div className="px-4 lg:px-6 flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-base font-semibold">Operaciones</p>
          <p className="text-sm text-muted-foreground">
            Cargas y viajes creados en el período
          </p>
        </div>

        {/* Period selector */}
        <div className="flex items-center rounded-lg border bg-muted/40 p-0.5 gap-0.5">
          {(["today", "week", "month"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                period === p
                  ? "bg-background shadow-xs text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {p === "today" ? "Hoy" : p === "week" ? "Esta semana" : "Este mes"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Cargas y Viajes (table) ──────────────────────────────────── */}
      <SubSection title="Cargas y Viajes">
        <div className="*:data-[slot=card]:bg-linear-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card *:data-[slot=card]:shadow-xs dark:*:data-[slot=card]:bg-card">
          <Card>
            <CardContent className="pt-4 pb-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left pb-2 font-medium text-muted-foreground w-1/4">
                      Línea
                    </th>
                    <th className="text-right pb-2 font-medium text-muted-foreground">
                      Viajes
                    </th>
                    <th className="text-right pb-2 font-medium text-muted-foreground">
                      Cargas
                    </th>
                    <th className="text-right pb-2 font-medium text-muted-foreground">
                      Eficiencia
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedRows.map((row, idx) => (
                    <tr
                      key={row.workLine}
                      className={idx < normalizedRows.length - 1 ? "border-b" : ""}
                    >
                      <td className="py-3 font-medium">
                        {WORK_LINE_LABELS[row.workLine]}
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold">
                        {isLoading ? (
                          "—"
                        ) : (
                          <>
                            <div>{row.trips.toLocaleString()}</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              ({row.totalKm.toLocaleString()} km)
                            </div>
                          </>
                        )}
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold">
                        {isLoading ? (
                          "—"
                        ) : (
                          <>
                            <div>{row.shipments.toLocaleString()}</div>
                            <div className="text-xs font-normal text-muted-foreground">
                              ({row.withCargoKm.toLocaleString()} km)
                            </div>
                          </>
                        )}
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold">
                        {isLoading
                          ? "—"
                          : efficiency(row.tripsWithCargo, row.trips)}
                      </td>
                    </tr>
                  ))}

                  {/* Totals row */}
                  <tr className="bg-muted/30 border-t">
                    <td className="py-3 font-semibold pl-1">Total</td>
                    <td className="py-3 text-right tabular-nums font-semibold">
                      {isLoading ? (
                        "—"
                      ) : (
                        <>
                          <div>{totals.trips.toLocaleString()}</div>
                          <div className="text-xs font-normal text-muted-foreground">
                            ({totals.totalKm.toLocaleString()} km)
                          </div>
                        </>
                      )}
                    </td>
                    <td className="py-3 text-right tabular-nums font-semibold">
                      {isLoading ? (
                        "—"
                      ) : (
                        <>
                          <div>{totals.shipments.toLocaleString()}</div>
                          <div className="text-xs font-normal text-muted-foreground">
                            ({totals.withCargoKm.toLocaleString()} km)
                          </div>
                        </>
                      )}
                    </td>
                    <td className="py-3 text-right tabular-nums font-semibold">
                      {isLoading
                        ? "—"
                        : efficiency(totals.tripsWithCargo, totals.trips)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </SubSection>

      {/* ── Eficiencia por línea (cards) ─────────────────────────────── */}
      <SubSection title="Eficiencia por línea">
        <div className="grid grid-cols-1 gap-4 @xl/main:grid-cols-3 *:data-[slot=card]:bg-linear-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card *:data-[slot=card]:shadow-xs dark:*:data-[slot=card]:bg-card">
          {normalizedRows.map((row) => {
            const pct = efficiencyPct(row.tripsWithCargo, row.trips)
            const label = WORK_LINE_LABELS[row.workLine]
            const colorClass = WORK_LINE_BADGE_CLASS[row.workLine]

            return (
              <Card key={row.workLine} className="@container/card">
                <CardHeader className="pb-2">
                  <CardDescription>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                        colorClass,
                      )}
                    >
                      {label}
                    </span>
                  </CardDescription>
                  <CardTitle className="text-3xl font-semibold tabular-nums">
                    {isLoading ? "—" : row.trips === 0 ? "—" : `${pct}%`}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 pb-4 flex flex-col gap-2">
                  <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: isLoading ? "0%" : `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isLoading ? (
                      "—"
                    ) : (
                      <>
                        {row.tripsWithCargo} con carga ·{" "}
                        <span className="text-amber-500">
                          {row.tripsWithoutCargo} sin carga
                        </span>
                      </>
                    )}
                  </p>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </SubSection>
    </div>
  )
}
